import { useEffect, useState, useSyncExternalStore } from "react";
import {
  prepareLocalImageSync,
  syncPreparedLocalImage,
  type LocalImageSync,
} from "@/api/sync-local-image";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { useT } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";
import { Button } from "./button";

export function AttachmentSyncButton({
  src,
  testId = "image-lightbox-sync",
}: {
  src: string;
  testId?: string;
}) {
  const t = useT(uiMessages);
  const scope = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    getDesktopChatAuthScope,
  );
  const [image, setImage] = useState<LocalImageSync | null>(null);
  const [state, setState] = useState<"idle" | "syncing" | "synced" | "error">(
    "idle",
  );
  useEffect(() => {
    let active = true;
    setImage(null);
    setState("idle");
    void prepareLocalImageSync(src)
      .then((value) => {
        if (active) setImage(value);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [src, scope]);
  if (!image || !image.isCurrent()) return null;
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={state === "syncing" || state === "synced"}
        data-testid={testId}
        onClick={() => {
          setState("syncing");
          void syncPreparedLocalImage(image)
            .then(() => {
              if (image.isCurrent()) setState("synced");
            })
            .catch(() => {
              if (image.isCurrent()) setState("error");
            });
        }}
      >
        {t(
          state === "syncing"
            ? "syncingImage"
            : state === "synced"
              ? "imageSynced"
              : "syncImage",
        )}
      </Button>
      {state === "error" ? (
        <span
          role="alert"
          className="text-appkit-small text-white"
          data-testid={`${testId}-error`}
        >
          {t("syncImageFailed")}
        </span>
      ) : null}
    </div>
  );
}
